import { useRef, useState, useEffect, useCallback } from 'react';
import { X, Check, Eraser, Pen, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import useApi from '../hooks/useApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_PRESETS = ['Réception', 'Magasinier', 'Acheteur', 'Gérant', 'Autre'];

/**
 * SignaturePad - Captures une signature client tactile + nom + role + GPS.
 * Applique la signature a TOUS les BOL scannes pour le stop_id donne.
 *
 * Props:
 *   - stopId (string, required)
 *   - reportId (string, optional)
 *   - bolCount (number) : nombre de BOL deja scannes pour ce stop (display only)
 *   - existing (object|null) : signature deja capturee (mode lecture / re-signer)
 *   - onClose (function)
 *   - onSigned (function(signaturePayload)) : callback apres succes
 */
export const SignaturePad = ({
  stopId,
  reportId = null,
  bolCount = 0,
  existing = null,
  onClose,
  onSigned,
}) => {
  const { post } = useApi();
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [name, setName] = useState(existing?.signed_by_name || '');
  const [role, setRole] = useState(existing?.signed_by_role || '');
  const [submitting, setSubmitting] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);

  // GPS capture (best-effort, non-blocking)
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsErr('GPS non supporte');
      return;
    }
    if (window.__IS_NATIVE__ === true && localStorage.getItem('propilote_location_consent') !== 'accepted') {
      setGpsErr('Localisation non autorisee');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setGpsErr(err.message || 'GPS refuse'),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }, []);

  // Setup canvas (HiDPI aware)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }, []);

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Saisir le nom du signataire');
      return;
    }
    if (!hasDrawn && !existing) {
      toast.error('La signature est requise');
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const payload = {
        stop_id: stopId,
        report_id: reportId,
        signed_by_name: name.trim(),
        signed_by_role: role.trim() || null,
        signature_data_url: dataUrl,
        gps_lat: gps?.lat ?? null,
        gps_lng: gps?.lng ?? null,
      };
      const result = await post('/delivery/sign', payload);
      if (!result.success) {
        toast.error(result.error || 'Erreur sauvegarde signature');
        return;
      }
      if (result.queued) {
        toast.success('Signature sauvegardée localement — synchronisation au retour réseau');
      } else {
        toast.success(`Signature enregistree — ${result.data.applied_to_bols} bon(s) signé(s)`);
      }
      onSigned?.(result.data);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Erreur sauvegarde signature');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      data-testid="signature-pad-overlay"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <Pen size={18} className="text-amber-400" />
              Signature client
            </h3>
            <p className="text-slate-400 text-xs mt-0.5">
              {bolCount > 0
                ? `S'applique aux ${bolCount} bon${bolCount > 1 ? 's' : ''} de cet arrêt`
                : 'BOL scannés ensuite seront automatiquement signés'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1"
            data-testid="signature-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-slate-300 text-xs">Nom du signataire *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Marc Tremblay"
              className="bg-slate-800 border-slate-700 text-white mt-1"
              data-testid="signature-name-input"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-slate-300 text-xs">Fonction (optionnel)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
              {ROLE_PRESETS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    role === r
                      ? 'bg-amber-500 text-black border-amber-500 font-medium'
                      : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                  }`}
                  data-testid={`signature-role-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Saisie libre"
              className="bg-slate-800 border-slate-700 text-white text-sm"
              data-testid="signature-role-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-slate-300 text-xs">Signature *</Label>
              <button
                type="button"
                onClick={clear}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                data-testid="signature-clear-btn"
              >
                <Eraser size={12} /> Effacer
              </button>
            </div>
            <canvas
              ref={canvasRef}
              className="w-full h-44 bg-white rounded-lg border-2 border-slate-700 touch-none cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
              data-testid="signature-canvas"
            />
            <p className="text-xs text-slate-500 mt-1">
              Faites signer le client avec le doigt directement sur l&apos;écran.
            </p>
          </div>

          {/* GPS feedback */}
          <div className="flex items-center gap-2 text-xs">
            <MapPin
              size={12}
              className={gps ? 'text-emerald-400' : 'text-slate-500'}
            />
            <span className={gps ? 'text-emerald-400' : 'text-slate-500'}>
              {gps
                ? `Position : ${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`
                : gpsErr
                  ? `GPS: ${gpsErr}`
                  : 'Capture position en cours...'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-700"
            data-testid="signature-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !name.trim() || !hasDrawn}
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-50"
            data-testid="signature-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Sauvegarde...
              </span>
            ) : (
              <>
                <Check size={16} className="mr-1" />
                Confirmer
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
